/**
 * EffectSelectorModal Component
 * 
 * Modal dialog for selecting an effect to add to a slot.
 * Displays effects organized by category with descriptions.
 */

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { EffectDefinition, EffectType } from '../../services/effects/EffectsManager';
import { announceToScreenReader, trapFocus } from '../../utils/accessibility';
import { getEffectUsage } from '../../constants/effectDescriptions';
import './EffectSelectorModal.css';

/**
 * Props for EffectSelectorModal component
 */
export interface EffectSelectorModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Available effect definitions */
  effects: EffectDefinition[];
  /** Callback when an effect is selected */
  onSelect: (type: EffectType) => void;
  /** Slot number being filled */
  slot: number;
  /** Row number */
  row: number;
}

/**
 * EffectSelectorModal component
 * 
 * Renders a modal dialog with:
 * - Grid of effect cards organized by category
 * - Effect name, description, and icon
 * - Click to select
 * 
 * @param props - Component props
 * @returns EffectSelectorModal component
 */
export function EffectSelectorModal({
  isOpen,
  onClose,
  effects,
  onSelect,
  slot,
  row,
}: EffectSelectorModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  /**
   * Announce modal opening and trap focus
   */
  useEffect(() => {
    if (isOpen) {
      announceToScreenReader(
        `Select an effect for slot ${slot + 1} on row ${row + 1}`,
        'polite'
      );
      
      // Trap focus within modal
      if (dialogRef.current) {
        const cleanup = trapFocus(dialogRef.current);
        return cleanup;
      }
    }
    return undefined;
  }, [isOpen, slot, row]);

  /**
   * Handle escape key to close modal
   */
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
    return undefined;
  }, [isOpen, onClose]);

  /**
   * Handle click outside modal to close
   */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen, onClose]);

  /**
   * Handle effect selection
   */
  const handleSelect = (type: EffectType) => {
    onSelect(type);
    onClose();
  };

  if (!isOpen) return null;

  // Group effects by category (case-insensitive)
  const distortionEffects = effects.filter(e => e.category?.toLowerCase() === 'distortion');
  const timeBasedEffects = effects.filter(e => e.category?.toLowerCase() === 'time-based');
  const modulationEffects = effects.filter(e => e.category?.toLowerCase() === 'modulation');
  const dynamicsEffects = effects.filter(e => e.category?.toLowerCase() === 'dynamics');
  const otherEffects = effects.filter(e => {
    const cat = e.category?.toLowerCase();
    return cat && !['distortion', 'time-based', 'modulation', 'dynamics'].includes(cat);
  });

  // Render modal using portal to break out of parent container
  return createPortal(
    <div
      className="effect-selector-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="effect-selector-title"
    >
      <div className="effect-selector-modal" ref={dialogRef}>
        <div className="effect-selector-header">
          <h2 id="effect-selector-title" className="effect-selector-title">
            Select Effect for Slot {slot + 1}
          </h2>
          <button
            className="effect-selector-close"
            onClick={onClose}
            aria-label="Close dialog"
            type="button"
          >
            ✕
          </button>
        </div>

        <div className="effect-selector-content">
          {/* Distortion Effects */}
          {distortionEffects.length > 0 && (
            <div className="effect-category">
              <h3 className="effect-category-title">
                <i className="fas fa-bolt" aria-hidden="true"></i>
                Distortion
              </h3>
              <div className="effect-grid">
                {distortionEffects.map(effect => {
                  const usage = getEffectUsage(effect.type);
                  return (
                    <button
                      key={effect.type}
                      className="effect-card"
                      onClick={() => handleSelect(effect.type)}
                      type="button"
                      title={usage}
                    >
                      <div className="effect-card-icon">
                        <i className="fas fa-wave-square" aria-hidden="true"></i>
                      </div>
                      <div className="effect-card-name">{effect.name}</div>
                      <div className="effect-card-description">{effect.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Time-Based Effects (Delay & Reverb) */}
          {timeBasedEffects.length > 0 && (
            <div className="effect-category">
              <h3 className="effect-category-title">
                <i className="fas fa-clock" aria-hidden="true"></i>
                Time-Based
              </h3>
              <div className="effect-grid">
                {timeBasedEffects.map(effect => {
                  const usage = getEffectUsage(effect.type);
                  return (
                    <button
                      key={effect.type}
                      className="effect-card"
                      onClick={() => handleSelect(effect.type)}
                      type="button"
                      title={usage}
                    >
                      <div className="effect-card-icon">
                        <i className="fas fa-history" aria-hidden="true"></i>
                      </div>
                      <div className="effect-card-name">{effect.name}</div>
                      <div className="effect-card-description">{effect.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Modulation Effects */}
          {modulationEffects.length > 0 && (
            <div className="effect-category">
              <h3 className="effect-category-title">
                <i className="fas fa-water" aria-hidden="true"></i>
                Modulation
              </h3>
              <div className="effect-grid">
                {modulationEffects.map(effect => {
                  const usage = getEffectUsage(effect.type);
                  return (
                    <button
                      key={effect.type}
                      className="effect-card"
                      onClick={() => handleSelect(effect.type)}
                      type="button"
                      title={usage}
                    >
                      <div className="effect-card-icon">
                        <i className="fas fa-sync-alt" aria-hidden="true"></i>
                      </div>
                      <div className="effect-card-name">{effect.name}</div>
                      <div className="effect-card-description">{effect.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Dynamics Effects (EQ, Compressor, etc.) */}
          {dynamicsEffects.length > 0 && (
            <div className="effect-category">
              <h3 className="effect-category-title">
                <i className="fas fa-sliders-h" aria-hidden="true"></i>
                Dynamics
              </h3>
              <div className="effect-grid">
                {dynamicsEffects.map(effect => {
                  const usage = getEffectUsage(effect.type);
                  return (
                    <button
                      key={effect.type}
                      className="effect-card"
                      onClick={() => handleSelect(effect.type)}
                      type="button"
                      title={usage}
                    >
                      <div className="effect-card-icon">
                        <i className="fas fa-sliders-h" aria-hidden="true"></i>
                      </div>
                      <div className="effect-card-name">{effect.name}</div>
                      <div className="effect-card-description">{effect.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Other Effects */}
          {otherEffects.length > 0 && (
            <div className="effect-category">
              <h3 className="effect-category-title">
                <i className="fas fa-star" aria-hidden="true"></i>
                Other
              </h3>
              <div className="effect-grid">
                {otherEffects.map(effect => {
                  const usage = getEffectUsage(effect.type);
                  return (
                    <button
                      key={effect.type}
                      className="effect-card"
                      onClick={() => handleSelect(effect.type)}
                      type="button"
                      title={usage}
                    >
                      <div className="effect-card-icon">
                        <i className="fas fa-magic" aria-hidden="true"></i>
                      </div>
                      <div className="effect-card-name">{effect.name}</div>
                      <div className="effect-card-description">{effect.description}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
